import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Message } from 'app/layout/common/messages/messages.types';
import { map, Observable, ReplaySubject, switchMap, take, tap, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MessagesService {
    private _messages: ReplaySubject<Message[]> = new ReplaySubject<Message[]>(1);

    // Static messages data for development - Healthcare/TMA focused
    private _staticMessages: Message[] = [
        {
            id: '1',
            image: 'images/logo/logo.svg',
            title: 'Welcome to TMA',
            description: 'Your billing analytics platform is ready. Start by uploading your data.',
            time: new Date().toISOString(),
            read: false
        },
        {
            id: '2',
            icon: 'heroicons_outline:document-arrow-up',
            title: 'Data Sync Complete',
            description: 'MEDENT integration synced 2,847 records successfully.',
            time: new Date(Date.now() - 3600000).toISOString(),
            read: false
        },
        {
            id: '3',
            icon: 'heroicons_outline:chart-bar',
            title: 'Monthly Report Available',
            description: 'Your December billing analytics report is ready for review.',
            time: new Date(Date.now() - 7200000).toISOString(),
            read: true
        }
    ];

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for messages
     */
    get messages$(): Observable<Message[]> {
        return this._messages.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all messages
     */
    getAll(): Observable<Message[]> {
        // Return static messages for development
        return of([...this._staticMessages]).pipe(
            tap((messages) => {
                this._messages.next(messages);
            })
        );

        // Original API call commented out for development
        /*
        return this._httpClient.get<Message[]>('api/common/messages').pipe(
            tap((messages) => {
                this._messages.next(messages);
            })
        );
        */
    }

    /**
     * Create a message
     *
     * @param message
     */
    create(message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) => {
                // For development: simulate creating a message
                const newMessage: Message = {
                    ...message,
                    id: Date.now().toString(),
                    time: new Date().toISOString(),
                    read: false
                };

                // Update local messages
                const updatedMessages = [...messages, newMessage];
                this._messages.next(updatedMessages);
                this._staticMessages = updatedMessages;

                return of(newMessage);

                // Original API call commented out
                /*
                return this._httpClient
                    .post<Message>('api/common/messages', { message })
                    .pipe(
                        map((newMessage) => {
                            this._messages.next([...messages, newMessage]);
                            return newMessage;
                        })
                    );
                */
            })
        );
    }

    /**
     * Update the message
     *
     * @param id
     * @param message
     */
    update(id: string, message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) => {
                // For development: simulate updating a message
                const index = messages.findIndex(item => item.id === id);
                if (index !== -1) {
                    const updatedMessage = { ...messages[index], ...message };
                    messages[index] = updatedMessage;
                    this._messages.next([...messages]);
                    this._staticMessages = [...messages];
                    return of(updatedMessage);
                }
                return of(message);

                // Original API call commented out
                /*
                return this._httpClient
                    .patch<Message>('api/common/messages', { id, message })
                    .pipe(
                        map((updatedMessage: Message) => {
                            const index = messages.findIndex((item) => item.id === id);
                            messages[index] = updatedMessage;
                            this._messages.next(messages);
                            return updatedMessage;
                        })
                    );
                */
            })
        );
    }

    /**
     * Delete the message
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) => {
                // For development: simulate deleting a message
                const index = messages.findIndex(item => item.id === id);
                if (index !== -1) {
                    messages.splice(index, 1);
                    this._messages.next([...messages]);
                    this._staticMessages = [...messages];
                }
                return of(true);

                // Original API call commented out
                /*
                return this._httpClient
                    .delete<boolean>('api/common/messages', { params: { id } })
                    .pipe(
                        map((isDeleted: boolean) => {
                            const index = messages.findIndex((item) => item.id === id);
                            messages.splice(index, 1);
                            this._messages.next(messages);
                            return isDeleted;
                        })
                    );
                */
            })
        );
    }

    /**
     * Mark all messages as read
     */
    markAllAsRead(): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) => {
                // For development: simulate marking all as read
                messages.forEach((message, index) => {
                    messages[index].read = true;
                });
                this._messages.next([...messages]);
                this._staticMessages = [...messages];
                return of(true);

                // Original API call commented out
                /*
                return this._httpClient
                    .get<boolean>('api/common/messages/mark-all-as-read')
                    .pipe(
                        map((isUpdated: boolean) => {
                            messages.forEach((message, index) => {
                                messages[index].read = true;
                            });
                            this._messages.next(messages);
                            return isUpdated;
                        })
                    );
                */
            })
        );
    }
}